import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronRight } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';

const FAQS = [
  {
    q: 'Do I need any experience?',
    a: (
      <p>
        None at all. Most of our drivers are beginners, and our team will get you fully set up before
        you hit the track. We start with forgiving driver assists and can turn up the realism and
        competition as you build confidence.
      </p>
    ),
  },
  {
    q: 'Can children race?',
    a: (
      <div className="space-y-3">
        <p>Yes. We run dedicated sessions tailored by age group:</p>
        <ul className="space-y-1.5 pl-1">
          <li>• Young Racer Experience: Ages 5–7 (first-timer rig)</li>
          <li>• Child Sim Racing Experience: Ages 8–15 (adapted child rig)</li>
          <li>• Adult Sim Racing Experience: Ages 16+ (rig of choice)</li>
        </ul>
        <p>
          Drivers under 18 must have parental or legal guardian consent, and a parent or guardian
          must remain on-site during the session for younger drivers. Racers must also comfortably
          reach the pedals and controls to drive safely.
        </p>
      </div>
    ),
  },
  {
    q: 'How much does a session cost?',
    a: (
      <div className="space-y-3">
        <ul className="space-y-1.5 pl-1">
          <li>• Young Racer Experience: £20 (30 minutes)</li>
          <li>• Child Sim Racing Experience: £25 (40-minute session incl. briefing & debrief)</li>
          <li>• Adult Sim Racing Experience: £35 (50 minutes)</li>
        </ul>
        <p>
          We also run regular membership and loyalty rewards for frequent drivers. Planning a
          corporate event, team social, or private venue hire? Contact us directly by email to
          arrange bespoke group packages.
        </p>
      </div>
    ),
  },
  {
    q: 'Can I enter competitions?',
    a: (
      <div className="space-y-2">
        <p>Yes. We run two regular competitions:</p>
        <ul className="space-y-1.5 pl-1">
          <li>• Monthly Free Draw: Open to all drivers ages 5+</li>
          <li>• Paid Sim Rig Draw (£5 entry): Entrants must be 18 or older</li>
        </ul>
      </div>
    ),
  },
  {
    q: 'How many people can race at once?',
    a: (
      <p>
        Up to 10 drivers can compete on track at the same time, making our venue ideal for group
        sessions, birthday parties, corporate events, and stag dos.
      </p>
    ),
  },
  {
    q: 'Can I book on the same day?',
    a: (
      <p>
        Advance online booking is recommended to secure your preferred rig and time slot, but check
        our booking page for live availability if you're looking for same-day racing.
      </p>
    ),
  },
  {
    q: 'What should I wear?',
    a: (
      <p>
        Wear comfortable casual clothes and flat shoes or trainers. No specialist racing gear is
        needed, and driving gloves are available on-site if you prefer using them.
      </p>
    ),
  },
  {
    q: 'What simulators do you use?',
    a: (
      <p>
        Our setups feature professional-grade force-feedback hardware running premier motorsport
        simulation titles, primarily iRacing and Assetto Corsa.
      </p>
    ),
  },
];

export default function Faq() {
  useSeo({
    title: 'FAQ — Motion Reality Sim Racing',
    description:
      'Answers to common questions about Motion Reality sim racing sessions: experience needed, age groups, pricing, competitions, group bookings and what to wear.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a.props.children
            ? Array.isArray(f.a.props.children)
              ? f.a.props.children.map((c) => (typeof c === 'string' ? c : '')).join(' ')
              : f.a.props.children
            : '',
        },
      })),
    },
  });

  return (
    <div className="min-h-screen bg-[#0a0a0c] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 px-3 py-1.5 mb-5">
              <HelpCircle className="w-3.5 h-3.5 text-primary" />
              <span className="text-primary text-[10px] font-bold tracking-widest">FAQ</span>
            </div>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-white/40 text-sm">
              Everything you need to know before your visit — sessions, pricing, age groups and
              venue logistics. Can't find your answer? Our team is one message away.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accordion */}
      <section className="px-6 lg:px-10 py-12">
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-white/10"
              >
                <AccordionTrigger className="text-white text-left text-sm md:text-base font-bold hover:no-underline hover:text-primary transition-colors py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/50 text-sm leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Still need help CTA */}
          <div className="mt-12 bg-white/[0.02] border border-white/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white text-sm font-bold mb-1">Still need help?</p>
              <p className="text-white/40 text-xs">
                Browse our support channels or send us a message directly.
              </p>
            </div>
            <Link
              to="/customer-service"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors shrink-0"
            >
              CONTACT SUPPORT <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}